import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, Signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faHandPointUp, faMessage } from '@fortawesome/free-regular-svg-icons';
import {
  faBuilding,
  faBullhorn,
  faChevronDown,
  faChevronLeft,
  faHouse,
  faLifeRing,
  faRocket,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { select, Store } from '@ngxs/store';
import { AppUrls } from '../../../../../common/dto/app-urls';
import { FeaturePermission, isFeatureEnabled } from '../../../../../common/model/feature-permissions.enum';
import { AppState } from '../../app.state';
import { PersistedAppState, SetNavOpenedAction } from '../../persisted-app.state';

interface NavGroup {
  title: string;
  link: string;
  expanded: boolean;
  icon: IconDefinition;
  items?: {
    title: string;
    link: string;
    condition?: Signal<boolean>;
  }[];
}

@Component({
  selector: 'app-dashboard-nav',
  imports: [
    FaIconComponent,
    RouterLink,
    RouterLinkActive,
    MatIconButton,
  ],
  templateUrl: './dashboard-nav.component.html',
  styleUrl: './dashboard-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardNavComponent {
  private _store = inject(Store);
  private _changeDetector = inject(ChangeDetectorRef);

  protected userSession = select(AppState.session);

  protected navOpen = select(PersistedAppState.navOpen);

  protected showBilling = computed(() => isFeatureEnabled(FeaturePermission.Permission_Subscriptions_Admin, this.userSession().permissions));

  protected navGroups: NavGroup[] = [
    {
      title: $localize`Home`,
      link: AppUrls.Dashboard._.Home.absoluteUrl,
      expanded: false,
      icon: faHouse,
    },
    {
      title: $localize`Announce`,
      link: AppUrls.Dashboard._.Announce.absoluteUrl,
      expanded: true,
      icon: faBullhorn,
      items: [
        {
          title: $localize`Popups`,
          link: AppUrls.Dashboard._.Announce._.Popups.absoluteUrl,
        },
        {
          title: $localize`Banners`,
          link: AppUrls.Dashboard._.Announce._.Banners.absoluteUrl,
        },
      ],
    },
    {
      title: $localize`Guide`,
      link: AppUrls.Dashboard._.Guide.absoluteUrl,
      expanded: true,
      icon: faHandPointUp,
      items: [
        {
          title: $localize`Hints & Tooltips`,
          link: AppUrls.Dashboard._.Guide._.Hints.absoluteUrl,
        },
        {
          title: $localize`Checklists`,
          link: AppUrls.Dashboard._.Guide._.Checklists.absoluteUrl,
        },
        {
          title: $localize`Elements`,
          link: AppUrls.Dashboard._.Guide._.Elements.absoluteUrl,
        },
      ],
    },
    {
      title: $localize`Help`,
      link: AppUrls.Dashboard._.Help.absoluteUrl,
      expanded: true,
      icon: faLifeRing,
      items: [
        {
          title: $localize`Articles`,
          link: AppUrls.Dashboard._.Help._.KnowledgeBase._.ArticleList.absoluteUrl,
        },
        {
          title: $localize`Knowledge bases`,
          link: AppUrls.Dashboard._.Help._.KnowledgeBase._.KnowledgeBaseList.absoluteUrl,
        },
      ],
    },
    {
      title: $localize`Feedback`,
      link: AppUrls.Dashboard._.Feedback.absoluteUrl,
      expanded: true,
      icon: faMessage,
      items: [
        {
          title: $localize`Forms`,
          link: AppUrls.Dashboard._.Feedback._.Forms._.FormList.absoluteUrl,
        },
      ],
    },
    {
      title: $localize`Discover`,
      link: AppUrls.Dashboard._.Discover.absoluteUrl,
      expanded: true,
      icon: faRocket,
      items: [
        {
          title: $localize`Users`,
          link: AppUrls.Dashboard._.Discover._.Visitors.absoluteUrl,
        },
        {
          title: $localize`Trends`,
          link: AppUrls.Dashboard._.Discover._.Trends.absoluteUrl,
        },
      ],
    },
    {
      title: $localize`Company`,
      link: AppUrls.Dashboard._.Company.absoluteUrl,
      expanded: true,
      icon: faBuilding,
      items: [
        {
          title: $localize`Sites`,
          link: AppUrls.Dashboard._.Company._.SiteList.absoluteUrl,
        },
        {
          title: $localize`Members`,
          link: AppUrls.Dashboard._.Company._.Members.absoluteUrl,
        },
        {
          title: $localize`Billing`,
          link: AppUrls.Dashboard._.Company._.Billing.absoluteUrl,
          condition: this.showBilling,
        },
      ],
    },
  ];

  public toggleGroupExpanded(group: NavGroup): void {
    group.expanded = !group.expanded;

    this._changeDetector.markForCheck();
  }

  public expandGroup(group: NavGroup): void {
    group.expanded = true;

    this._changeDetector.markForCheck();
  }

  protected toggleNavOpen() {
    this._store.dispatch(new SetNavOpenedAction(!this.navOpen()));
  }

  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faChevronDown = faChevronDown;
}
